import { SkeletonLine } from '@cloudflare/kumo';

/** Estados de carregamento: o esqueleto reproduz o formato do conteúdo em vez de um spinner. */

interface BaseProps {
  /** Texto lido por leitores de tela (o esqueleto em si é decorativo). */
  label?: string;
  className?: string;
}

function Wrap({ label = 'Carregando', className, children }: BaseProps & { children: React.ReactNode }) {
  return (
    <div role="status" aria-busy="true" aria-label={label} className={className}>
      <span className="sr-only">{label}</span>
      <div aria-hidden="true">{children}</div>
    </div>
  );
}

/** Linhas de texto (parágrafo ou lista curta). */
export function SkeletonText({ lines = 3, label, className }: BaseProps & { lines?: number }) {
  return (
    <Wrap label={label} className={className}>
      <div className="flex flex-col gap-3">
        {Array.from({ length: lines }, (_, i) => <SkeletonLine key={i} minWidth={i === lines - 1 ? 30 : 60} maxWidth={100} blockHeight={16} />)}
      </div>
    </Wrap>
  );
}

/** Tabela: cabeçalho e linhas com as mesmas colunas da real. */
export function SkeletonTable({ rows = 6, cols = 4, label, className }: BaseProps & { rows?: number; cols?: number }) {
  return (
    <Wrap label={label} className={className}>
      <div className="flex flex-col">
        {Array.from({ length: rows }, (_, r) => (
          <div key={r} className="grid items-center gap-4 border-b border-kumo-hairline px-4 py-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
            {Array.from({ length: cols }, (_, c) => <SkeletonLine key={c} minWidth={c === 0 ? 50 : 30} maxWidth={c === 0 ? 90 : 70} blockHeight={16} />)}
          </div>
        ))}
      </div>
    </Wrap>
  );
}

/** Cartões em grade (métricas, catálogos). */
export function SkeletonCards({ count = 6, label, className }: BaseProps & { count?: number }) {
  return (
    <Wrap label={label} className={className}>
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="flex flex-col gap-3 rounded-lg border border-kumo-hairline p-4">
            <SkeletonLine minWidth={40} maxWidth={70} blockHeight={20} />
            <SkeletonLine minWidth={70} maxWidth={100} blockHeight={14} />
            <SkeletonLine minWidth={50} maxWidth={90} blockHeight={14} />
          </div>
        ))}
      </div>
    </Wrap>
  );
}

/** Lista vertical com avatar e duas linhas (conversas, chamados, sessões). */
export function SkeletonList({ rows = 5, label, className }: BaseProps & { rows?: number }) {
  return (
    <Wrap label={label} className={className}>
      <div className="flex flex-col gap-4 p-3">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="skeleton-line h-9 w-9 shrink-0 rounded-full" style={{ ['--skeleton-width' as string]: '100%' }} />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <SkeletonLine minWidth={40} maxWidth={80} blockHeight={14} />
              <SkeletonLine minWidth={60} maxWidth={100} blockHeight={12} />
            </div>
          </div>
        ))}
      </div>
    </Wrap>
  );
}

/** Página inteira: título, subtítulo e um bloco de conteúdo. */
export function PageSkeleton({ label = 'Carregando a página', cards = true }: { label?: string; cards?: boolean }) {
  return (
    <Wrap label={label} className="px-4 pt-7 md:px-10">
      <div className="mb-6 flex flex-col gap-3">
        <SkeletonLine minWidth={20} maxWidth={30} blockHeight={28} />
        <SkeletonLine minWidth={40} maxWidth={55} blockHeight={14} />
      </div>
      {cards ? <SkeletonCards count={6} label={label} /> : <SkeletonTable rows={6} cols={4} label={label} />}
    </Wrap>
  );
}

/** Coluna do funil: três cartões. */
export function SkeletonKanbanCards({ count = 3 }: { count?: number }) {
  return (
    <Wrap label="Carregando coluna">
      <div className="flex flex-col gap-2">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="flex flex-col gap-2 rounded-md border border-kumo-hairline p-3">
            <SkeletonLine minWidth={50} maxWidth={90} blockHeight={14} />
            <SkeletonLine minWidth={30} maxWidth={50} blockHeight={12} />
          </div>
        ))}
      </div>
    </Wrap>
  );
}
